import { z } from "zod";

/**
 * The token vocabulary a shared landing page renders a store from.
 *
 * This is deliberately the *small* list — the one a page actually consumes.
 * `apps/landings/bestkebab/src/tokens.css` declares a large private palette
 * (`--char`, `--chili`, `--sign`, …) and then maps exactly seven of those
 * names plus three font stacks onto what it calls "the kit's vocabulary":
 * `--ground`, `--surface`, `--line`, `--accent`, `--accent-strong`, `--ink`,
 * `--ink-soft`, `--font-display`, `--font-body`, `--font-mono`. Those ten are
 * what its components read; the rest never leave that file. So those ten are
 * what a store can be branded by from the database, and nothing more.
 *
 * Everything is optional with a default, because a store with no theme at all
 * — a shop onboarded five minutes ago — still has to render a legible page.
 * `ThemeSchema.parse({})` therefore yields a complete theme, which is also why
 * `Theme` (the *output* type) has no optional members: a consumer that has
 * parsed a theme can read every token without a null check.
 *
 * Re-introduced deliberately. It was dropped from the phase 1 contract work
 * (plan task 9, spec ruling 11) on YAGNI grounds: with both landings bespoke,
 * nothing read a theme blob. The shared landing engine is that consumer, so
 * the reasoning that dropped it no longer holds — see
 * `docs/superpowers/specs/2026-08-22-better-auth-multistore-design.md`.
 */

/**
 * A colour a landing page can drop straight into a CSS custom property.
 *
 * Restricted to hex rather than "any CSS colour" on purpose. These values are
 * written into a `<style>` block or an inline `style` attribute as
 * `--accent: <value>`, so the grammar of what may appear there is a security
 * boundary, not a formatting preference: `red; } body { … }` is a perfectly
 * good CSS colour followed by an escape. Three, six and eight digits are all
 * accepted (`#rgb`, `#rrggbb`, `#rrggbbaa`) because all three are what a
 * designer hands over; nothing else can express a rule terminator.
 *
 * The cost is that `rgba(…)` translucency cannot be stored. Both live stores
 * keep their translucent values (`--glow`, `--lk-scrim`) as private tokens
 * derived in CSS from these, never as part of the vocabulary a page brands
 * itself by, so nothing is lost today.
 */
export const HexColourSchema = z
  .string()
  .regex(
    /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/,
    "must be a hex colour: #rgb, #rrggbb or #rrggbbaa",
  );

/**
 * A CSS font-family list, e.g. `"DM Sans", system-ui, sans-serif`.
 *
 * Same reasoning as `HexColourSchema`, and the same boundary: this ends up as
 * the right-hand side of a custom property. The character class allows exactly
 * what a font stack needs — family names, the quotes around a name with a
 * space, the commas between them — and excludes every character that could
 * close a declaration or open a new one (`;`, `{`, `}`, `(`, `)`, `<`, `>`,
 * `/`, `\`, `@`). `url()`, `@import` and comment sequences are unspellable as
 * a result.
 */
export const FontStackSchema = z
  .string()
  .min(1)
  .max(160)
  .regex(
    /^[A-Za-z0-9 ,'"._-]+$/,
    "may contain only font family names, quotes, commas and spaces",
  );

/**
 * The fallback stacks. System fonts only: a store that has expressed no
 * preference must not make its customers wait on a network font, and the
 * shared engine has no way to know which web font to load for a name it was
 * never given.
 */
const DEFAULT_SANS = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const DEFAULT_MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

/**
 * The unbranded fallback palette.
 *
 * Chosen for contrast, not for character: a warm neutral dark ground with a
 * single restrained amber accent. It is meant to look plain rather than to
 * look designed — a store that has not chosen colours should read as one that
 * has not chosen colours yet, not as one wearing somebody else's brand.
 * `accentStrong` is a denser version of `accent` rather than a second hue,
 * matching what the name means in both live stores: the fill a solid control
 * gets when `accent` alone is too light to sit text on.
 */
export const ThemeSchema = z.object({
  /** The page behind everything. */
  ground: HexColourSchema.default("#141210"),
  /** A panel lifted off the ground: a card, the cart, the footer. */
  surface: HexColourSchema.default("#1e1b18"),
  /** A visible hairline between things. */
  line: HexColourSchema.default("#332f2a"),
  /** The brand colour: the sign, a highlight, the focus ring. */
  accent: HexColourSchema.default("#e5b93c"),
  /** The same role with enough body to fill a button and carry text. */
  accentStrong: HexColourSchema.default("#c8901c"),
  /** Body text on `ground` and `surface`. */
  ink: HexColourSchema.default("#f2ede4"),
  /** Secondary text: captions, the closed-now line, footer detail. */
  inkSoft: HexColourSchema.default("#b3aa9c"),
  /** Headings and the logotype. */
  fontDisplay: FontStackSchema.default(DEFAULT_SANS),
  /** Everything else. */
  fontBody: FontStackSchema.default(DEFAULT_SANS),
  /** Prices, order numbers, anything that should line up in a column. */
  fontMono: FontStackSchema.default(DEFAULT_MONO),
});
export type Theme = z.infer<typeof ThemeSchema>;
